import { fetchAirtableRecord } from "../airtable/FetchAirtableRecord";
import { AirtableRecordResponse, AirtableTableId } from "../airtable/types";
import { fetchRailData } from "../FetchRailData";
import { PageData } from "./types";

/**
 * Fetches complete page data including all rails and their associated ATOM assets
 *
 * @remarks
 * This utility orchestrates the data fetching for a complete page in the Peacock platform.
 * It bridges Airtable CMS data with the ATOM API to build a fully hydrated page structure:
 *
 * 1. Takes an Airtable Page record (which contains Collection IDs)
 * 2. Fetches each Collection record from Airtable
 * 3. For each Collection, fetches the associated ATOM asset data via {@link fetchRailData}
 * 4. Returns a complete PageData structure with the page metadata and all rails with assets
 *
 * This is typically used when rendering pages where the page structure is managed in Airtable
 * but the actual content comes from the ATOM API.
 *
 * **Important**: This function will throw an error if the provided record is not a valid
 * Page record (i.e., missing the `Collections` field). Ensure you're passing a record
 * fetched from the Pages table in Airtable.
 *
 * @param page - AirtableRecordResponse for a Page record from Airtable (must contain Collections field)
 * @returns Promise that resolves to a PageData object containing the page record and an array of RailData with ATOM assets
 * @throws Error if the `Collections` field is undefined (indicating an invalid Page record)
 *
 * @example
 * ```typescript
 * // Fetch a page record from Airtable
 * const pageRecord = await fetchAirtableRecord(`${AirtableTableId.Pages}/rec123`);
 *
 * // Get complete page data with all rails and assets
 * const pageData = await fetchPageData(pageRecord);
 *
 * // pageData.self contains the Airtable page record
 * // pageData.rails contains an array of RailData, each with:
 * //   - self: the Airtable Collection record
 * //   - assets: array of AssetData with ATOM API data
 * ```
 */
const fetchPageData = async (
  page: AirtableRecordResponse,
): Promise<PageData> => {
  if ("Collections" in page.fields && page.fields.Collections !== undefined) {
    const railIds = page.fields.Collections;

    const rails = await Promise.all(
      railIds.map((id) =>
        fetchAirtableRecord(`${AirtableTableId.Collections}/${id}`),
      ),
    );

    const assetResults = await Promise.all(rails.map(fetchRailData));
    const assets = assetResults.filter((asset) => asset !== null);

    return { self: page, rails: assets };
  } else {
    throw new Error(`'Collections' is undefined for the page ${page.id}`);
  }
};

export { fetchPageData };
